import React from "react";
import CRUDTable from "../CRUDTable";
import { protocolCRUD } from "../../../http";
import { asJSONSafe } from "../../../functions";
import ProtocolReceiversSection from "../../../sections/ProtocolReceiversSection";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

const iconStyle = {
    display: "inline-flex",
    alignItems: "center",
};

const normalizeBool = (value) => {
    if (value === true || value === false) return value;
    if (value === 1 || value === 0) return Boolean(value);
    if (value === "true" || value === "false") return value === "true";
    return null;
};

const renderBoolIcon = (value) => {
    const normalized = normalizeBool(value);
    if (normalized === null) return "";
    return (
        <span
            style={{
                ...iconStyle,
                color: normalized ? "#2e7d32" : "#d32f2f",
            }}
        >
            {normalized ? <CheckIcon fontSize="small" /> : <CloseIcon fontSize="small" />}
        </span>
    );
};

const renderJsonOrBool = (value) => {
    if (value == null || value === "") return "";
    const parsed = asJSONSafe(value);
    const normalized = normalizeBool(parsed);
    if (normalized !== null) return renderBoolIcon(normalized);
    if (parsed == null || parsed === "") return "";
    if (typeof parsed === "object") return JSON.stringify(parsed);
    return String(parsed);
};

const getRowValue = (row, key) => {
    if (row?.[key] !== undefined) return row[key];
    const lowerKey = `${key[0].toLowerCase()}${key.slice(1)}`;
    if (row?.[lowerKey] !== undefined) return row[lowerKey];
    return undefined;
};

const getReceivers = (row) => getRowValue(row, "Receivers") ?? getRowValue(row, "receivers");

const getReceiverField = (row, field) => {
    const direct = getRowValue(row, field);
    if (direct !== undefined) return direct;
    const receivers = asJSONSafe(getReceivers(row));
    if (!receivers) return undefined;
    if (receivers?.[field] !== undefined) return receivers[field];
    const matchKey = Object.keys(receivers).find(
        (key) => key.toLowerCase() === field.toLowerCase()
    );
    return matchKey ? receivers[matchKey] : undefined;
};

const buildReceiversFallback = (row) => {
    const base = getReceivers(row);
    if (base !== undefined) return base;
    const fallback = {
        EHealthReceived: getRowValue(row, "EHealthReceived"),
        EHealthReceivedTime: getRowValue(row, "EHealthReceivedTime"),
        EHealthIds: getRowValue(row, "EHealthIds"),
        EHealthErrors: getRowValue(row, "EHealthErrors"),
        LmkReceived: getRowValue(row, "LmkReceived"),
        LmkReceivedTime: getRowValue(row, "LmkReceivedTime"),
        LmkIds: getRowValue(row, "LmkIds"),
        LmkErrors: getRowValue(row, "LmkErrors"),
        ESenReceived: getRowValue(row, "ESenReceived"),
        ESenReceivedTime: getRowValue(row, "ESenReceivedTime"),
        ESenIds: getRowValue(row, "ESenIds"),
        ESenErrors: getRowValue(row, "ESenErrors"),
    };
    const hasAnyValue = Object.values(fallback).some((value) => value !== undefined);
    return hasAnyValue ? fallback : undefined;
};

export default function EHealthProtocolCRUDTable({ ...props }) {
    const columns = [
        {
            title: "Id",
            dataIndex: "id",
            key: "id",
            render: (value, row) => value ?? row?.Id,
            width: 90,
        },
        {
            title: "Receivers",
            dataIndex: "receivers",
            key: "receivers",
            render: (value, row) => {
                const receivers = buildReceiversFallback(row) ?? value;
                return <ProtocolReceiversSection data={receivers} />;
            },
        },
        {
            title: "EHealthReceived",
            dataIndex: "EHealthReceived",
            key: "EHealthReceived",
            render: (value, row) => renderJsonOrBool(getReceiverField(row, "EHealthReceived") ?? value),
            width: 180,
        },
        {
            title: "LmkReceived",
            dataIndex: "LmkReceived",
            key: "LmkReceived",
            render: (value, row) => renderBoolIcon(getReceiverField(row, "LmkReceived") ?? value),
            width: 120,
        },
    ];

    const searchFields = [
        {
            title: "Id",
            key: "id",
            compareType: "equals",
            operandPosition: 1,
            filterType: "normal",
        },
        {
            title: "Номер протокола",
            key: "number",
            compareType: "equals",
            operandPosition: 1,
            filterType: "normal",
        },
    ];

    const booleanOptions = [
        { id: false, name: "Нет" },
        { id: true, name: "Да" },
    ];

    const filterFields = [
        {
            title: "EHealthReceived",
            key: "EHealthReceived",
            compareType: "equals",
            operandPosition: 1,
            type: "normal",
            filterType: "normal",
            optionLabelKey: "name",
            options: booleanOptions,
        },
        {
            title: "LmkReceived",
            key: "LmkReceived",
            compareType: "equals",
            operandPosition: 1,
            type: "normal",
            filterType: "normal",
            optionLabelKey: "name",
            options: booleanOptions,
        },
    ];

    return (
        <CRUDTable
            search={protocolCRUD.search}
            columns={columns}
            searchFields={searchFields}
            filterFields={filterFields}
            scroll={{ x: "max-content" }}
            pagination
            {...props}
        />
    );
}
